import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import { unwrap } from '@/api/envelope';
import { useGetModerationHistoryByContent } from '@/api/generated/moderation/moderation';
import type { AdminModerationHistoryResponseDTO } from '@/api/generated/model';
import { ModerationHistoryList } from '@/components/ModerationHistoryList';
import { PaginationBar } from '@/components/data-grid';
import { useUserDetail } from '../api';
import { ROUTES } from '@/app/routes';

const PAGE_SIZE = 20;

/** Full moderation history for a user (TM-3515 preview link target). */
export function UserModerationHistoryPage() {
  const { userId = '' } = useParams();
  const id = Number(userId);
  const valid = Number.isFinite(id) && id > 0;
  const [page, setPage] = useState(1);

  const userQuery = useUserDetail(userId);
  const historyQuery = useGetModerationHistoryByContent(
    id,
    { contentType: 'U' },
    {
      query: {
        enabled: valid,
        select: (res) => unwrap<AdminModerationHistoryResponseDTO[] | undefined>(res) ?? [],
      },
    },
  );

  const entries = historyQuery.data ?? [];
  const totalPage = Math.max(1, Math.ceil(entries.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [userId]);

  useEffect(() => {
    if (page > totalPage) setPage(totalPage);
  }, [page, totalPage]);

  const pageEntries = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return entries.slice(start, start + PAGE_SIZE);
  }, [entries, page]);

  const displayName =
    userQuery.data?.displayName?.trim() ||
    userQuery.data?.uName?.trim() ||
    (valid ? `User #${userId}` : 'User');

  return (
    <div className="flex flex-col gap-4 p-6">
      <nav className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link to={ROUTES.users} className="hover:text-foreground">
          User Management
        </Link>
        <ChevronRight className="size-4" />
        {valid ? (
          <Link to={ROUTES.userDetails(userId)} className="hover:text-foreground">
            {displayName}
          </Link>
        ) : (
          <span>User</span>
        )}
        <ChevronRight className="size-4" />
        <span className="text-foreground">Moderation History</span>
      </nav>

      <div>
        <h1 className="text-lg font-semibold">Moderation History</h1>
        <p className="text-sm text-muted-foreground">
          All moderation actions related to {displayName}
          {historyQuery.data != null ? ` (${historyQuery.data.length})` : ''}.
        </p>
      </div>

      {!valid ? (
        <p className="text-sm text-muted-foreground">User not found.</p>
      ) : userQuery.isLoading && !userQuery.data ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <ModerationHistoryList
          entries={pageEntries}
          isLoading={historyQuery.isLoading}
          isError={historyQuery.isError}
          onRetry={() => void historyQuery.refetch()}
          footer={
            !historyQuery.isLoading && !historyQuery.isError && entries.length > PAGE_SIZE ? (
              <PaginationBar
                page={page}
                totalPage={totalPage}
                onPageChange={setPage}
                isFetching={historyQuery.isFetching}
                className="mt-2 border-t-0 pb-0"
              />
            ) : null
          }
        />
      )}
    </div>
  );
}
